import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '../components/ui/skeleton';
import TrendingSection from '../components/trending/TrendingSection';
import TopTopicsBlock from '../components/trending/TopTopicsBlock';
import HeroImageSlideshow from '../components/layout/HeroImageSlideshow';
import { BookOpen } from 'lucide-react';
import { slugify } from '../lib/slugify';
import {
  useArticleSections,
  groupArticlesBySection,
  articleToTrendingItem,
  TOP_ARTICLES_N,
} from '../lib/articlesHierarchy';

// Same layout as How Does It Work?: a site-wide Trending row, then one block per section
// (heading links to that section's page) showing its top articles plus a "More Articles"
// link when the section has more than fit.
const ArticlesPage = () => {
  const { articles, loading } = useArticleSections();

  const trendingArticleItems = useMemo(() => articles.map(articleToTrendingItem), [articles]);
  const sections = useMemo(() => groupArticlesBySection(articles), [articles]);

  return (
    <div className="min-h-screen" data-testid="articles-page">
      {/* Hero Section */}
      <section className="bg-chassis text-white py-16 md:py-24">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-6">
              <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-4">
                Automotive Knowledge Platform
              </p>
              <h1 className="font-chivo text-4xl md:text-6xl font-black uppercase tracking-tighter mb-6">
                Technical Articles
              </h1>
              <p className="text-lg text-white/70 max-w-xl leading-relaxed">
                Explore in-depth technical articles on automotive systems, engineering principles, and industry best practices from domain experts.
              </p>
            </div>
            <div className="md:col-span-6 hidden md:block self-stretch">
              <HeroImageSlideshow alt="Technical Articles" />
            </div>
          </div>
        </div>
      </section>

      <TrendingSection sectionType="article" items={trendingArticleItems} />

      {/* Sections */}
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-loading">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 w-full" />
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16" data-testid="no-articles-message">
              <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-chivo text-xl font-bold uppercase mb-2">No Articles Yet</h3>
              <p className="text-muted-foreground">Check back soon for new technical articles.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {sections.map(([section, sectionArticles]) => {
                const sectionSlug = slugify(section);
                return (
                  <div key={section} data-testid={`articles-section-${sectionSlug}`}>
                    <div className="flex items-center justify-between mb-4">
                      <Link to={`/articles/section/${sectionSlug}`} className="group">
                        <h2 className="font-chivo text-2xl font-bold uppercase tracking-tight group-hover:text-autoxium-blue transition-colors">
                          {section}
                        </h2>
                      </Link>
                      <p className="font-mono text-sm text-muted-foreground">
                        {sectionArticles.length} article{sectionArticles.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <TopTopicsBlock
                      sectionType="article"
                      items={sectionArticles.map(articleToTrendingItem)}
                      hasMore={sectionArticles.length > TOP_ARTICLES_N}
                      moreTopicsPath={`/articles/section/${sectionSlug}/more`}
                      testId={`articles-${sectionSlug}`}
                      size="sm"
                      topN={TOP_ARTICLES_N}
                      title="Top Articles"
                      moreLabel="More Articles"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ArticlesPage;
